import React from 'react';
import { motion } from 'framer-motion';
import { PageHero } from '../components/layout/PageHero';
import { Card } from '../components/ui/Card';
import { SectionHeading } from '../components/ui/SectionHeading';
import { Button } from '../components/ui/Button';
import { TargetIcon, GlobeIcon, UsersIcon, HandshakeIcon, AwardIcon, BuildingIcon, ArrowRightIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
const pillars = [{
  icon: HandshakeIcon,
  title: 'Strategic Partnerships',
  description: 'Building bridges between the African Union, member states, and global partners to advance shared development priorities.'
}, {
  icon: GlobeIcon,
  title: 'Global Representation',
  description: "Representing the African Union's interests across the United Kingdom, Europe, and the wider international community."
}, {
  icon: TargetIcon,
  title: 'Project Delivery',
  description: 'Coordinating and overseeing transformative projects in health, education, trade, infrastructure, and innovation.'
}, {
  icon: AwardIcon,
  title: 'Transparent Procurement',
  description: 'Conducting fair, competitive procurement processes that uphold AU standards of integrity and accountability.'
}];
const leadership = [{
  name: 'H.E. Dr. Adaeze Okonkwo',
  role: 'Regional Director',
  bio: 'Over 25 years of experience in international development, diplomacy, and African Union strategic initiatives.',
  initials: 'AO'
}, {
  name: 'Mr. Kwame Mensah',
  role: 'Head of Procurement',
  bio: 'Procurement and contract management specialist with extensive experience in multilateral institutions.',
  initials: 'KM'
}, {
  name: 'Dr. Fatima Diallo',
  role: 'Head of Programmes',
  bio: 'Programme delivery expert leading flagship projects across health, education, and economic development sectors.',
  initials: 'FD'
}, {
  name: 'Ms. Sarah Mwangi',
  role: 'Head of Partnerships',
  bio: 'Cultivating strategic partnerships with governments, multilateral agencies, the private sector, and civil society.',
  initials: 'SM'
}];
const timeline = [{
  year: '2015',
  title: 'Establishment',
  description: "The African Union Commission expanded its global presence to deepen Africa's engagement with international partners."
}, {
  year: '2018',
  title: 'Expanded Mandate',
  description: 'Mandate expanded to include procurement coordination and project delivery across AU member states.'
}, {
  year: '2020',
  title: 'Strategic Framework Launch',
  description: 'Launched a five-year strategic framework focused on trade, health, education, and infrastructure.'
}, {
  year: '2023',
  title: 'AfCFTA Acceleration',
  description: 'Played a key role in supporting the African Continental Free Trade Area implementation worldwide.'
}, {
  year: '2025',
  title: 'Global Partnerships Era',
  description: 'Launched the Global Partnerships Initiative connecting Africa with worldwide investors and institutions.'
}, {
  year: '2026',
  title: 'Today',
  description: 'Coordinating £180M+ in active projects across 28 member states with 45+ ongoing initiatives.'
}];
export function About() {
  return <div data-id="element-431">
      <PageHero title="About Us" subtitle="The African Union Commission — bridging Africa and the world" breadcrumbs={[{
      label: 'Home',
      path: '/'
    }, {
      label: 'About'
    }]} data-id="element-432" />

      <section className="py-16 bg-white" data-id="element-433">
        <div className="max-w-7xl mx-auto px-4" data-id="element-434">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-435">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} data-id="element-436">
              <h2 className="text-3xl md:text-4xl font-serif text-charcoal mb-4" data-id="element-437">
                Who We Are
              </h2>
              <div className="w-16 h-1 bg-au-gold mb-6" data-id="element-438"></div>
              <p className="text-gray-700 text-lg leading-relaxed mb-4" data-id="element-439">
                The African Union Commission is the Secretariat of the African
                Union, entrusted with executive functions across the continent.
                We serve as a strategic platform connecting Africa's 55 member
                states with global partners — including governments,
                multilateral institutions, the private sector, civil society,
                and the African diaspora.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed mb-6" data-id="element-440">
                We coordinate flagship projects, manage procurement processes,
                foster partnerships, and represent the African Union's interests
                on the international stage. Our work supports the realisation of
                Agenda 2063 — Africa's blueprint for inclusive and sustainable
                development.
              </p>
              <Link to="/projects" data-id="element-441">
                <Button variant="primary" className="flex items-center gap-2" data-id="element-442">
                  Explore Our Projects
                  <ArrowRightIcon size={18} data-id="element-443" />
                </Button>
              </Link>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="grid grid-cols-2 gap-4" data-id="element-444">
              <Card className="bg-au-green text-white border-au-green text-center py-8" data-id="element-445">
                <div className="text-4xl font-bold mb-2" data-id="element-446">55</div>
                <div className="text-sm text-au-green-light" data-id="element-447">
                  AU Member States
                </div>
              </Card>
              <Card className="bg-white text-center py-8" data-id="element-448">
                <div className="text-4xl font-bold text-au-green mb-2" data-id="element-449">45+</div>
                <div className="text-sm text-gray-600" data-id="element-450">Active Projects</div>
              </Card>
              <Card className="bg-white text-center py-8" data-id="element-451">
                <div className="text-4xl font-bold text-au-green mb-2" data-id="element-452">
                  £180M
                </div>
                <div className="text-sm text-gray-600" data-id="element-453">Project Portfolio</div>
              </Card>
              <Card className="bg-au-green text-white border-au-green text-center py-8" data-id="element-454">
                <div className="text-4xl font-bold mb-2" data-id="element-455">150+</div>
                <div className="text-sm text-au-green-light" data-id="element-456">
                  Partner Suppliers
                </div>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-ivory" data-id="element-457">
        <div className="max-w-7xl mx-auto px-4" data-id="element-458">
          <div className="grid md:grid-cols-2 gap-8" data-id="element-459">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} data-id="element-460">
              <Card className="h-full" data-id="element-461">
                <div className="w-14 h-14 bg-au-green rounded-lg flex items-center justify-center mb-4" data-id="element-462">
                  <TargetIcon size={28} className="text-white" data-id="element-463" />
                </div>
                <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-464">
                  Our Mission
                </h3>
                <div className="w-12 h-1 bg-au-gold mb-4" data-id="element-465"></div>
                <p className="text-gray-700 leading-relaxed" data-id="element-466">
                  To advance the African Union's vision by fostering strategic
                  partnerships, coordinating impactful projects, and ensuring
                  transparent procurement that delivers tangible benefits to
                  African citizens and the global community.
                </p>
              </Card>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} data-id="element-467">
              <Card className="h-full" data-id="element-468">
                <div className="w-14 h-14 bg-au-green rounded-lg flex items-center justify-center mb-4" data-id="element-469">
                  <GlobeIcon size={28} className="text-white" data-id="element-470" />
                </div>
                <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-471">
                  Our Vision
                </h3>
                <div className="w-12 h-1 bg-au-gold mb-4" data-id="element-472"></div>
                <p className="text-gray-700 leading-relaxed" data-id="element-473">
                  An integrated, prosperous, and peaceful Africa — driven by its
                  own citizens and representing a dynamic force on the global
                  stage, as envisioned by Agenda 2063.
                </p>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white" data-id="element-474">
        <div className="max-w-7xl mx-auto px-4" data-id="element-475">
          <SectionHeading title="Our Mandate" subtitle="Four strategic pillars guide our work in advancing Africa's global partnerships" centered data-id="element-476" />

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12" data-id="element-477">
            {pillars.map((pillar, index) => {
            const Icon = pillar.icon;
            return <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: index * 0.1
            }} data-id="element-478">
                  <Card hover className="h-full text-center" data-id="element-479">
                    <div className="w-16 h-16 bg-au-green-light rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-480">
                      <Icon size={32} className="text-au-green" data-id="element-481" />
                    </div>
                    <h3 className="font-bold text-charcoal mb-2 text-lg" data-id="element-482">
                      {pillar.title}
                    </h3>
                    <p className="text-sm text-gray-600" data-id="element-483">
                      {pillar.description}
                    </p>
                  </Card>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      <section className="py-16 bg-ivory" data-id="element-484">
        <div className="max-w-7xl mx-auto px-4" data-id="element-485">
          <SectionHeading title="Leadership Team" subtitle="Experienced leaders driving Africa's global engagement and project delivery" centered data-id="element-486" />

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12" data-id="element-487">
            {leadership.map((leader, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: index * 0.1
          }} data-id="element-488">
                <Card hover className="text-center h-full" data-id="element-489">
                  <div className="w-24 h-24 bg-au-green rounded-full flex items-center justify-center mx-auto mb-4 ring-4 ring-au-green-light" data-id="element-490">
                    <span className="text-white text-2xl font-serif" data-id="element-491">
                      {leader.initials}
                    </span>
                  </div>
                  <h3 className="font-bold text-charcoal mb-1" data-id="element-492">
                    {leader.name}
                  </h3>
                  <div className="text-sm font-medium text-au-green mb-3" data-id="element-493">
                    {leader.role}
                  </div>
                  <p className="text-sm text-gray-600" data-id="element-494">{leader.bio}</p>
                </Card>
              </motion.div>)}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white" data-id="element-495">
        <div className="max-w-7xl mx-auto px-4" data-id="element-496">
          <SectionHeading title="Organisational Structure" subtitle="A streamlined structure designed for effective delivery and accountability" centered data-id="element-497" />

          <div className="mt-12 max-w-4xl mx-auto" data-id="element-498">
            <div className="flex flex-col items-center" data-id="element-499">
              <Card className="bg-au-green text-white border-au-green text-center w-full max-w-md mb-6" data-id="element-500">
                <BuildingIcon size={32} className="mx-auto mb-2" data-id="element-501" />
                <div className="text-sm text-au-green-light mb-1" data-id="element-502">
                  Office of the
                </div>
                <div className="font-bold text-lg" data-id="element-503">Regional Director</div>
              </Card>

              <div className="w-px h-8 bg-gray-border" data-id="element-504"></div>

              <div className="grid md:grid-cols-3 gap-4 w-full" data-id="element-505">
                {['Programmes', 'Procurement', 'Partnerships'].map((dept, i) => <Card key={i} hover className="text-center" data-id="element-506">
                      <div className="w-12 h-12 bg-au-green-light rounded-lg flex items-center justify-center mx-auto mb-3" data-id="element-507">
                        <UsersIcon size={24} className="text-au-green" data-id="element-508" />
                      </div>
                      <div className="font-bold text-charcoal mb-1" data-id="element-509">
                        Department of
                      </div>
                      <div className="text-au-green font-serif text-xl" data-id="element-510">
                        {dept}
                      </div>
                    </Card>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-ivory" data-id="element-511">
        <div className="max-w-7xl mx-auto px-4" data-id="element-512">
          <SectionHeading title="Our History" subtitle="A decade of advancing Africa's global partnerships" centered data-id="element-513" />

          <div className="mt-12 max-w-4xl mx-auto" data-id="element-514">
            <div className="relative" data-id="element-515">
              <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-1 bg-au-green-light md:-translate-x-1/2" data-id="element-516"></div>

              <div className="space-y-8" data-id="element-517">
                {timeline.map((item, index) => <motion.div key={index} initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.4,
                delay: index * 0.05
              }} className={`relative flex items-start md:items-center gap-6 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`} data-id="element-518">
                    <div className="absolute left-8 md:left-1/2 w-4 h-4 bg-au-gold rounded-full -translate-x-1/2 ring-4 ring-ivory z-10 mt-2 md:mt-0" data-id="element-519"></div>

                    <div className={`pl-20 md:pl-0 md:w-1/2 ${index % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'}`} data-id="element-520">
                      <Card hover data-id="element-521">
                        <div className="text-2xl font-serif text-au-green mb-1" data-id="element-522">
                          {item.year}
                        </div>
                        <h3 className="font-bold text-charcoal mb-2" data-id="element-523">
                          {item.title}
                        </h3>
                        <p className="text-sm text-gray-600" data-id="element-524">
                          {item.description}
                        </p>
                      </Card>
                    </div>

                    <div className="hidden md:block md:w-1/2" data-id="element-525"></div>
                  </motion.div>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-au-green text-white" data-id="element-526">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-527">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} data-id="element-528">
            <h2 className="text-3xl md:text-4xl font-serif mb-4" data-id="element-529">
              Partner With Us
            </h2>
            <div className="w-24 h-1 bg-au-gold mx-auto mb-6" data-id="element-530"></div>
            <p className="text-xl text-au-green-light mb-8" data-id="element-531">
              Whether you're a supplier, investor, or institution, there are
              many ways to engage with the African Union Commission.
            </p>
            <div className="flex flex-wrap gap-4 justify-center" data-id="element-532">
              <Link to="/procurement/supplier-registration" data-id="element-533">
                <Button variant="secondary" className="bg-white" data-id="element-534">
                  Submit Expression of Interest
                </Button>
              </Link>
              <Link to="/contact" className="inline-flex items-center justify-center px-6 py-3 rounded-lg font-medium border-2 border-white text-white bg-transparent hover:bg-white hover:text-au-green transition-colors duration-300" data-id="element-535">
                Get in Touch
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
}